<template>
  <v-container fluid pt-0>
    <v-row align="center" justify="end">
      <v-col sm="3" class="py-0">
        <v-select
          v-if="showGroupedView"
          :items="groupByOptions"
          v-model="selectedGroupedBy"
          label="Group by"
          item-text="name"
          return-object
          @change="onSelectedGroupByChange"
          :outlined="outlinedInputs"
          hide-details
        ></v-select>
      </v-col>
      <v-switch
        prepend-icon="mdi-view-list"
        class="mr-3"
        label="Group View"
        v-model="showGroupedView"
        @change="onShowGroupedViewChange"
      ></v-switch>
      <div class="pr-2">
        <list-visible-columns-and-filter
          :visible-columns="visibleColumns"
          :selectable-headers="selectableHeaders"
          v-model="search"
          @change="onVisibleColumnsChange"
        ></list-visible-columns-and-filter>
      </div>
      <add-taxes-dialog
        :services="services"
        :service-type="supplierServiceType"
        @save="onAddTaxes"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            color="primary"
            :disabled="$store.getters.hasAccessTo('suppliers:view_only')"
          >
            <v-icon left>mdi-plus-circle</v-icon>Add Taxes
          </v-btn>
        </template>
      </add-taxes-dialog>
    </v-row>
    <v-row v-if="showGroupedView">
      <v-col class="pa-0">
        <list-grouped :group="selectedGroupedBy" :items="taxes">
          <template v-slot:table="{ items }">
            <v-card outlined>
              <taxes-table
                :items="items"
                :headers="filteredHeaders"
                :search="search"
                :hide-footer="true"
                @edit="onEdit"
                @delete="onDelete"
              ></taxes-table>
            </v-card>
          </template>
        </list-grouped>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col class="pa-0">
        <v-card outlined>
          <taxes-table
            :items="taxes"
            :headers="filteredHeaders"
            :search="search"
            @edit="onEdit"
            @delete="onDelete"
          ></taxes-table>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="debug">
      <pre>{{ supplier }}</pre>
    </v-row>
    <tax-item-editor
      v-model="editedItem"
      :show="showTaxEditor"
      @input="onTaxItemSave"
      @close="showTaxEditor = false"
    />
  </v-container>
</template>

<script>
import { removeGQLTypename } from "../../../../helpers/index";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import ListGrouped from "../../shared/ListGrouped";
import AddTaxesDialog from "./AddTaxesDialog";
import { ListMixin } from "../../../../mixins/ListMixin";
import TaxesTable from "./TaxesTable";
import TaxItemEditor from "./TaxItemEditor";
import gql from "graphql-tag";
export const UPDATE_TAX_MUTATION = gql`
  mutation updateTax($id: ID!, $serviceTaxInput: ServiceTaxInput) {
    updateServiceTax(id: $id, serviceTaxInput: $serviceTaxInput) {
      id
    }
  }
`;
export const DELETE_TAX_MUTATION = gql`
  mutation deleteTax($id: ID!) {
    deleteServiceTax(id: $id) {
      id
    }
  }
`;
export const ADD_TAXES_MUTATION = gql`
  mutation addTaxes($supplierId: ID!, $taxes: [SupplierAddTaxesItemInput]!) {
    supplierAddTaxes(supplierId: $supplierId, taxes: $taxes) {
      success
      error
    }
  }
`;
const QUERY = gql`
  query supplierTaxesList($supplierId: ID!) {
    supplier(id: $supplierId) {
      id
      serviceType
      taxes {
        totalCount
        edges {
          node {
            id
            name
            code
            percent
            validFrom
            validTo
            service {
              id
              name
            }
          }
        }
      }
    }
  }
`;
export default {
  name: "supplier-taxes",
  props: {
    showTitle: {
      type: Boolean,
      default: () => true,
    },
    supplierId: {
      type: String,
      default: "",
    },
    services: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    ListVisibleColumnsAndFilter,
    ListGrouped,
    TaxesTable,
    AddTaxesDialog,
    TaxItemEditor,
  },
  mixins: [ListMixin],
  data() {
    return {
      debug: false,
      showGroupedView: this.$store.getters.localSettings(
        `${this.$options.name}-showGroupedView`,
        false
      ),
      showTaxEditor: false,
      search: "",
      editedItem: null,
      visibleColumns: this.$store.getters.localSettings(this.$options.name, [
        "name",
        "code",
        "validFrom",
        "validTo",
        "%",
        "service",
      ]),
      headers: [
        {
          text: "Valid From",
          sortable: false,
          value: "validFrom",
          name: "validFrom",
        },
        {
          text: "Valid To",
          sortable: false,
          value: "validTo",
          name: "validTo",
        },
        {
          text: "Name",
          sortable: true,
          value: "name",
          name: "name",
        },
        {
          text: "Code",
          sortable: true,
          value: "code",
          name: "code",
        },
        {
          text: "%",
          sortable: true,
          value: "percent",
          name: "percent",
        },
        {
          text: "Service",
          sortable: true,
          value: "service.name",
          name: "service",
        },
        {
          text: "Actions",
          sortable: false,
          value: "actions",
          name: "actions",
          align: "center",
        },
      ],
      selectedGroupedBy: {
        name: "Service",
        getKey: (item) => item.service.name,
        getLabel: (item) => item.service.name,
      },
      groupByOptions: [
        {
          name: "Service",
          getKey: (item) => item.service.name,
          getLabel: (item) => item.service.name,
        },
        {
          name: "Vat Code",
          getKey: (item) => item.code,
          getLabel: (item) => item.code,
        },
        {
          name: "Valid From",
          getKey: (item) => item.validFrom,
          getLabel: (item) =>
            `Valid from: ${this.$options.filters.formatDate(item.validFrom)}`,
        },
        {
          name: "Valid From - Valid To",
          getKey: (item) => `${item.validFrom}-${item.validTo}`,
          getLabel: (item) =>
            `Valid from: ${this.$options.filters.formatDate(
              item.validFrom
            )}, to: ${this.$options.filters.formatDate(item.validTo)}`,
        },
      ],
    };
  },
  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    taxes() {
      return this.supplier && this.supplier.taxes
        ? [...this.supplier.taxes.edges.map(removeGQLTypename)]
        : [];
    },
    supplierServiceType() {
      return this.supplier ? this.supplier.serviceType : "ACCOMMODATION";
    },
  },

  methods: {
    onSelectedGroupByChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        [`${this.$options.name}-selectedGroupBy`]: event,
      });
    },
    onShowGroupedViewChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        [`${this.$options.name}-showGroupedView`]: event,
      });
    },
    onEdit(data) {
      this.showTaxEditor = true;
      this.editedItem = { ...data };
    },
    async onTaxItemSave(payload) {
      try {
        const result = await this.$apollo.mutate({
          mutation: UPDATE_TAX_MUTATION,
          variables: {
            id: this.editedItem.id,
            serviceTaxInput: {
              name: this.editedItem.name,
              percent: parseFloat(this.editedItem.percent),
            },
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Discount updated",
        });
        //console.log(result);
        this.showTaxEditor = false;
      } catch (error) {
        console.log(error);
      }
    },
    async onDelete(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: DELETE_TAX_MUTATION,
          variables: {
            id: data.id,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Tax removed",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
    async onAddTaxes(data) {
      try {
        const result = await this.$apollo.mutate({
          mutation: ADD_TAXES_MUTATION,
          variables: {
            supplierId: this.supplierId,
            taxes: data,
          },
          refetchQueries: [
            { query: QUERY, variables: { supplierId: this.supplierId } },
          ],
        });
        this.$store.dispatch("showAppSnackbar", {
          msg: "Taxes added",
        });
        console.log(result);
      } catch (error) {
        console.log(error);
      }
    },
  },
  apollo: {
    supplier: {
      query: QUERY,
      variables() {
        return {
          supplierId: this.supplierId,
        };
      },
      fetchPolicy: "network-only",
      skip() {
        return !this.supplierId;
      },
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/components/gt/suppliers/taxes/SupplierTaxes.vue